import { ListIcon, UserIcon } from '@phosphor-icons/react';
import { Link, useLocation } from 'react-router-dom';

import NotificationsPopover from '@/components/blocks/NotificationsPopover';
import { Button } from '@/components/ui/button';
import Container from '@/components/ui/container';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import Logo from '@/components/ui/Logo';
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
  navigationMenuTriggerStyle,
} from '@/components/ui/navigation-menu';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import ThemeToggle from '@/components/ui/theme-toggle';
import config from '@/config/environment';
import { useAuth } from '@/contexts/AuthContext';
import { useFilteredMenu } from '@/hooks/use-filtered-menu';
import { useIsMobile } from '@/hooks/use-mobile';

export default function Header() {
  const { logout, user } = useAuth();
  const isMobile = useIsMobile();
  const location = useLocation();
  const filteredMenu = useFilteredMenu();

  const renderNavigationMenu = () => (
    <NavigationMenu>
      <NavigationMenuList>
        {filteredMenu.map(menuItem => (
          <NavigationMenuItem key={menuItem.slug}>
            {menuItem.hasSubMenu ? (
              <>
                <NavigationMenuTrigger>{menuItem.label}</NavigationMenuTrigger>
                <NavigationMenuContent>
                  <ul className='grid w-[200px] gap-2 p-4'>
                    {menuItem.subMenu?.map(subItem => (
                      <li key={subItem.slug}>
                        <NavigationMenuLink asChild>
                          <Link
                            to={subItem.path}
                            className={`block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground ${
                              location.pathname === subItem.path
                                ? 'bg-accent text-accent-foreground'
                                : ''
                            }`}
                          >
                            {subItem.label}
                          </Link>
                        </NavigationMenuLink>
                      </li>
                    ))}
                  </ul>
                </NavigationMenuContent>
              </>
            ) : (
              <NavigationMenuLink asChild>
                <Link
                  to={menuItem.path}
                  className={
                    navigationMenuTriggerStyle() +
                    ` ${
                      location.pathname === menuItem.path
                        ? 'bg-accent text-accent-foreground'
                        : ''
                    }`
                  }
                >
                  {menuItem.label}
                </Link>
              </NavigationMenuLink>
            )}
          </NavigationMenuItem>
        ))}
      </NavigationMenuList>
    </NavigationMenu>
  );

  const renderMobileMenu = () => (
    <Sheet>
      <SheetTrigger asChild>
        <Button
          color='gray'
          hierarchy='link'
          size='sm'
          className='md:hidden'
          aria-label='Open menu'
        >
          <ListIcon className='h-5 w-5' />
        </Button>
      </SheetTrigger>
      <SheetContent side='left' className='w-[300px] p-0'>
        <div className='flex flex-col gap-4 p-6'>
          <Logo sm />
          <nav className='flex flex-col space-y-2'>
            {filteredMenu.map(menuItem =>
              menuItem.hasSubMenu ? (
                <DropdownMenu key={menuItem.slug}>
                  <DropdownMenuTrigger asChild>
                    <Button
                      color='gray'
                      hierarchy='link'
                      className='w-full justify-start'
                    >
                      {menuItem.label}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className='w-56'>
                    {menuItem.subMenu?.map(subItem => (
                      <DropdownMenuItem key={subItem.slug} asChild>
                        <Link
                          to={subItem.path}
                          className={`w-full px-2 py-1.5 text-sm ${
                            location.pathname === subItem.path
                              ? 'bg-accent text-accent-foreground'
                              : ''
                          }`}
                        >
                          {subItem.label}
                        </Link>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <Link
                  key={menuItem.slug}
                  to={menuItem.path}
                  className={`flex w-full items-center justify-start rounded-md px-3 py-2 text-sm font-medium transition-colors hover:bg-accent hover:text-accent-foreground ${
                    location.pathname === menuItem.path
                      ? 'bg-accent text-accent-foreground'
                      : ''
                  }`}
                >
                  {menuItem.label}
                </Link>
              )
            )}
          </nav>
        </div>
      </SheetContent>
    </Sheet>
  );

  return (
    <header className='sticky top-0 z-40 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60'>
      <Container className='h-16'>
        <div className='flex h-full items-center justify-between gap-4'>
          <div className='flex items-center gap-4 md:gap-8'>
            <Logo sm />
            <nav className='hidden md:flex'>{renderNavigationMenu()}</nav>
            {isMobile && renderMobileMenu()}
          </div>

          <div className='flex items-center gap-2'>
            {config.features.darkMode && <ThemeToggle />}
            <NotificationsPopover />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  color='gray'
                  hierarchy='link'
                  size='sm'
                  className='relative'
                  aria-label='User menu'
                >
                  <UserIcon className='h-5 w-5' />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align='end' className='w-56'>
                <DropdownMenuLabel>
                  <div className='flex flex-col space-y-1'>
                    <p className='text-sm font-medium leading-none'>
                      {user?.username || 'User'}
                    </p>
                    <p className='text-xs leading-none text-muted-foreground'>
                      {user?.email || 'user@example.com'}
                    </p>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link to='/settings'>Settings</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to='/notifications'>Notifications</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to='/system-logs'>System Logs</Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logout}>Sign Out</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </Container>
    </header>
  );
}
